import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useUpload } from "@/hooks/use-upload";
import {
  useCimPackage, useSaveCimPackage, useTestSendCim, formatBytes, WARN_EMAIL_BYTES, HARD_EMAIL_BYTES,
  isConsentError, openConsentFlow,
  type CimDoc, type CimEmailDraft,
} from "@/hooks/useCimPackage";

const MERGE_FIELDS = [
  { token: "{{first_name}}", label: "First name" },
  { token: "{{buyer_name}}", label: "Buyer name" },
  { token: "{{client_name}}", label: "Client name" },
] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: string;
}

const emptyEmail = (): CimEmailDraft => ({ subject: "", body: "", documentIds: [], cc: [] });

const parseCcText = (text: string): string[] =>
  text.split(",").map((s) => s.trim()).filter((s) => s.includes("@"));

function sizeOf(email: CimEmailDraft, docs: CimDoc[]): number {
  const byId = new Map(docs.map((d) => [d.id, d]));
  return email.documentIds.reduce((s, id) => s + (byId.get(id)?.fileSize ?? 0), 0);
}

function SizeMeter({ bytes }: { bytes: number }) {
  const pct = Math.min(100, (bytes / HARD_EMAIL_BYTES) * 100);
  const over = bytes > HARD_EMAIL_BYTES;
  const warn = !over && bytes > WARN_EMAIL_BYTES;
  const cls = over ? "text-red-600 dark:text-red-400" : warn ? "text-amber-600 dark:text-amber-400" : "text-slate-500";
  const bar = over ? "bg-red-500" : warn ? "bg-amber-400" : "bg-green-500";
  return (
    <span className="flex items-center gap-2">
      <span className="relative h-1.5 w-24 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-900/30">
        <span className={`absolute inset-y-0 left-0 ${bar}`} style={{ width: `${pct}%` }} />
        {/* 25 MB safe-zone tick */}
        <span className="absolute inset-y-0 border-l border-slate-300 dark:border-slate-800" style={{ left: `${(WARN_EMAIL_BYTES / HARD_EMAIL_BYTES) * 100}%` }} />
      </span>
      <span className={`text-[11px] font-semibold tabular-nums ${cls}`}>
        {formatBytes(bytes)} of 25 MB
        {over && " — over the limit (email encoding adds ~35%); split into a second email or use smaller files"}
        {warn && " — past the 18 MB safe zone; may bounce at recipients with 25 MB caps, consider a second email"}
      </span>
    </span>
  );
}

export function CimPackageDialog({ open, onClose, clientId }: Props) {
  const { data, isLoading } = useCimPackage(clientId, open);
  const save = useSaveCimPackage(clientId);
  const testSend = useTestSendCim(clientId);
  const queryClient = useQueryClient();
  const { uploadFile, isUploading } = useUpload();
  const [emails, setEmails] = useState<CimEmailDraft[]>([emptyEmail()]);
  const [ccTexts, setCcTexts] = useState<string[]>([""]);
  const [pendingDeleteDoc, setPendingDeleteDoc] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Merge-field chips insert into the last-focused field ("i-subject" | "i-body")
  const fieldRefs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadTargetEmail, setUploadTargetEmail] = useState(0);

  useEffect(() => {
    if (open && data && !loaded) {
      // Prune document ids whose row no longer exists — a stale id is
      // invisible in the picker but fails validation on every save/send,
      // bricking the package with no way to fix it from the UI.
      const validIds = new Set(data.docs.map((d) => d.id));
      const loadedEmails = data.package?.emails?.length
        ? data.package.emails.map((e) => ({ ...e, documentIds: e.documentIds.filter((id) => validIds.has(id)) }))
        : [emptyEmail()];
      setEmails(loadedEmails);
      setCcTexts(loadedEmails.map((e) => (e.cc ?? []).join(", ")));
      setLoaded(true);
    }
  }, [open, data, loaded]);
  useEffect(() => { if (!open) setLoaded(false); }, [open]);

  const docs = data?.docs ?? [];
  const setEmail = (i: number, patch: Partial<CimEmailDraft>) =>
    setEmails((p) => p.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  const insertMergeField = (i: number, token: string) => {
    const key = focusKey?.startsWith(`${i}-`) ? focusKey : `${i}-body`;
    const field: "subject" | "body" = key.endsWith("subject") ? "subject" : "body";
    const el = fieldRefs.current[key];
    const current = emails[i]?.[field] ?? "";
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    setEmail(i, { [field]: current.slice(0, start) + token + current.slice(end) } as Partial<CimEmailDraft>);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const handleUploadFile = async (file: File) => {
    const uploaded = await uploadFile(file);
    if (!uploaded) { toast.error("Upload failed — nothing was saved"); return; }
    const res = await fetch("/api/documents", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name: file.name.replace(/\.[^/.]+$/, ""),
        fileName: file.name,
        fileUrl: uploaded.objectPath,
        fileSize: file.size,
        mimeType: file.type || "application/octet-stream",
        category: "cim",
        companyId: clientId,
      }),
    });
    const doc = await res.json().catch(() => null);
    if (!res.ok || !doc?.id) { toast.error(doc?.error || "Upload succeeded but the document row failed"); return; }
    await queryClient.invalidateQueries({ queryKey: ["cim-package", clientId] });
    // Attach to the email whose Upload button was clicked; if that email was
    // removed mid-upload, fall back to email 1 so the doc never vanishes.
    setEmails((p) => {
      const target = Math.min(uploadTargetEmail, p.length - 1);
      return p.map((e, j) => (j === target ? { ...e, documentIds: [...e.documentIds, doc.id] } : e));
    });
    toast.success(`${file.name} uploaded and attached`);
  };

  const toggleDoc = (i: number, docId: string) =>
    setEmails((p) => p.map((e, j) => {
      if (j === i) {
        return e.documentIds.includes(docId)
          ? { ...e, documentIds: e.documentIds.filter((d) => d !== docId) }
          : { ...e, documentIds: [...e.documentIds, docId] };
      }
      // A document lives in exactly one email — picking it here removes it from the other
      return { ...e, documentIds: e.documentIds.filter((d) => d !== docId) };
    }));

  const canSave = emails.every((e, i) =>
    e.subject.trim() && e.body.trim() && (i > 0 || e.documentIds.length > 0) && sizeOf(e, docs) <= HARD_EMAIL_BYTES,
  ) && !save.isPending;

  const withCc = () => emails.map((e, i) => ({ ...e, cc: parseCcText(ccTexts[i] ?? "") }));

  const handleSave = () => {
    save.mutate(withCc(), {
      onSuccess: () => { toast.success("CIM package saved"); onClose(); },
      onError: (e) => toast.error(e.message),
    });
  };

  const deleteDocument = async (docId: string) => {
    setPendingDeleteDoc(null);
    const res = await fetch(`/api/documents/${docId}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      toast.error(data?.error || "Failed to delete document");
      return;
    }
    setEmails((p) => p.map((e) => ({ ...e, documentIds: e.documentIds.filter((d) => d !== docId) })));
    await queryClient.invalidateQueries({ queryKey: ["cim-package", clientId] });
    toast.success("Document deleted");
  };

  const emailSection = (i: number) => {
    const email = emails[i];
    return (
      <div key={i} className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-900">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold text-slate-900 dark:text-slate-200">Email {i + 1}{i === 1 && <span className="ml-2 text-[11px] font-normal text-slate-400">for oversized packages</span>}</div>
          <div className="flex items-center gap-3">
            <SizeMeter bytes={sizeOf(email, docs)} />
            {i === 1 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 dark:text-red-400" onClick={() => { setEmails((p) => [p[0]]); setCcTexts((p) => [p[0] ?? ""]); }}>
                Remove
              </Button>
            )}
          </div>
        </div>
        <Input
          ref={(el) => { fieldRefs.current[`${i}-subject`] = el; }}
          placeholder="Subject — e.g. Confidential Information Memorandum for {{buyer_name}}"
          value={email.subject}
          onFocus={() => setFocusKey(`${i}-subject`)}
          onChange={(e) => setEmail(i, { subject: e.target.value })}
        />
        <Textarea
          ref={(el) => { fieldRefs.current[`${i}-body`] = el; }}
          rows={6}
          placeholder={"Body — plain text; blank line starts a new paragraph."}
          value={email.body}
          onFocus={() => setFocusKey(`${i}-body`)}
          onChange={(e) => setEmail(i, { body: e.target.value })}
        />
        <Input
          className="h-8 text-sm"
          placeholder="Cc on every send of this email (optional, comma-separated)"
          value={ccTexts[i] ?? ""}
          onChange={(e) => setCcTexts((p) => p.map((t, j) => (j === i ? e.target.value : t)))}
        />
        <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
          Insert:
          {MERGE_FIELDS.map((f) => (
            <button
              key={f.token}
              type="button"
              className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-semibold text-slate-600 hover:bg-blue-50 hover:text-blue-700 dark:border-slate-900 dark:bg-slate-950/40 dark:text-slate-400 dark:bg-blue-950/40 dark:text-blue-400"
              title={`Insert ${f.token} at the cursor`}
              onMouseDown={(e) => e.preventDefault() /* keep field focus */}
              onClick={() => insertMergeField(i, f.token)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Attachments</span>
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[11px]"
              disabled={isUploading}
              onClick={() => { setUploadTargetEmail(i); fileInputRef.current?.click(); }}
            >
              {isUploading ? "Uploading…" : "+ Upload document"}
            </Button>
          </div>
          {docs.length === 0 && (
            <div className="text-xs text-slate-400">No documents on this client yet — upload one right here.</div>
          )}
          <div className="max-h-44 space-y-1 overflow-y-auto">
            {docs.map((d) => {
              const checked = email.documentIds.includes(d.id);
              const inOther = emails.some((e, j) => j !== i && e.documentIds.includes(d.id));
              return (
                <label
                  key={d.id}
                  className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm ${!d.ok ? "cursor-not-allowed opacity-50" : checked ? "bg-blue-50 dark:bg-blue-950/40" : "hover:bg-slate-50 dark:bg-slate-950/40"}`}
                >
                  <input type="checkbox" disabled={!d.ok} checked={checked} onChange={() => toggleDoc(i, d.id)} />
                  <span className="flex-1 truncate">{d.name}</span>
                  {!d.ok && <span className="rounded bg-amber-50 px-1 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">re-upload needed</span>}
                  {inOther && <span className="text-[10px] text-slate-400">in email {i === 0 ? 2 : 1}</span>}
                  <span className="text-[11px] tabular-nums text-slate-400">{formatBytes(d.fileSize)}</span>
                  {pendingDeleteDoc === d.id ? (
                    <button
                      type="button"
                      className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white"
                      onClick={(e) => { e.preventDefault(); void deleteDocument(d.id); }}
                    >
                      Delete?
                    </button>
                  ) : (
                    <button
                      type="button"
                      title="Delete this document from the client"
                      className="px-1 text-slate-300 hover:text-red-600 dark:text-red-400"
                      onClick={(e) => { e.preventDefault(); setPendingDeleteDoc(d.id); setTimeout(() => setPendingDeleteDoc((p) => (p === d.id ? null : p)), 4000); }}
                    >
                      ✕
                    </button>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>CIM Package</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500">
          Set this up once per client. "Send CIM" on a tracker row sends these email(s) with the documents attached, from your own Outlook.
        </div>
        {isLoading ? (
          <div className="p-6 text-center text-slate-400">Loading…</div>
        ) : (
          <div className="space-y-4">
            {emails.map((_, i) => emailSection(i))}
            {emails.length === 1 && (
              <Button variant="outline" size="sm" onClick={() => { setEmails((p) => [...p, emptyEmail()]); setCcTexts((p) => [...p, ""]); }}>
                + Add second email
              </Button>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleUploadFile(f);
            e.target.value = "";
          }}
        />

        <div className="text-[11px] text-slate-400">
          Your signature (name, firm, address, office number) and the firm disclaimer are added to every email automatically.
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!canSave || testSend.isPending}
            title="Saves the package, then sends it to your own inbox exactly as a buyer would get it — cc recipients and the compliance bcc included, subject prefixed [TEST]"
            onClick={() =>
              save.mutate(withCc(), {
                onSuccess: () =>
                  testSend.mutate(undefined, {
                    onSuccess: (d) => {
                      const ccAll = withCc().flatMap((e) => e.cc ?? []);
                      toast.success(`Test sent to ${d.sentTo}${ccAll.length ? ` and cc'd ${ccAll.join(", ")}` : ""} — check your inbox`);
                    },
                    onError: (e) =>
                      isConsentError(e.message)
                        ? toast.error("Microsoft needs a one-time permission approval before the CRM can send mail with attachments.", {
                            duration: 15000,
                            action: { label: "Grant access", onClick: openConsentFlow },
                          })
                        : toast.error(e.message),
                  }),
                onError: (e) => toast.error(e.message),
              })
            }
          >
            {testSend.isPending ? "Sending test…" : "Email me a test"}
          </Button>
          <Button size="sm" disabled={!canSave} onClick={handleSave}>
            {save.isPending ? "Saving…" : "Save package"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
